import {memo, useMemo, useState} from "react";
import {useNavigate, useOutletContext} from "react-router-dom";
import {Badge, Box, Circle, Flex, HStack, Icon, IconButton, Stack, Table, Text} from "@chakra-ui/react";
import {MenuContent, MenuItem, MenuRoot, MenuTrigger} from "../../../../../components/ui/menu";
import {PlusIcon, ChevronRightIcon} from "@heroicons/react/16/solid";
import {EllipsisVerticalIcon, PencilSquareIcon, TrashIcon} from "@heroicons/react/20/solid";
import {Button} from "../../../../../components/ui/button";
import {ASSERTIONS, COTABDS, PROCESS_RISKS_AND_CONTROLS, PROCESS_FINDINGS} from "../mockData";
import type {Control, EvaluationScore, ProcessesOutletContext, ProcessStatus, Risk} from "../types";
import {useFindingsState, useRisksState} from "../hooks";
import {FindingsCardList} from "./FindingsCards";
import type {ProcessHeaderProps} from "../types";
import {ProcessHeader, StatusPicker} from "./ProcessHeader";
import RiskScorePopover from "./RiskScorePopover";
import {MockFilterBar} from "./FsaContent";
import {DataGrid} from "./DataGrid";

// --- Types ---

type RisksControlsCallbacks = {
  risks: Risk[];
  allControls: Control[];
  addRisk: () => void;
  deleteRisk: (riskId: string) => void;
  addControl: () => void;
  deleteControl: (controlId: string) => void;
  toggleMapping: (riskId: string, controlId: string) => void;
  updateRiskStatus: (riskId: string, status: ProcessStatus) => void;
  updateControlStatus: (controlId: string, status: ProcessStatus) => void;
};

// --- Constants ---

const SCORE_LEVELS: {label: string; color: string}[] = [
  {label: "Ineffective", color: "red.500"},
  {label: "Partially Effective", color: "orange.400"},
  {label: "Effective", color: "green.500"},
];

const RISK_GRID_COLUMNS = "1fr 180px 180px 180px 200px 58px";
const CONTROL_GRID_COLUMNS = "1fr 180px 180px 130px 58px";

// --- Components ---

const CotabdCell = memo(({cotabdAssertions}: {cotabdAssertions: Risk["cotabdAssertions"]}) => {
  const grouped = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const ca of cotabdAssertions) {
      if (!map.has(ca.cotabdId)) map.set(ca.cotabdId, []);
      map.get(ca.cotabdId)!.push(ca.assertionId);
    }
    return map;
  }, [cotabdAssertions]);

  if (grouped.size === 0)
    return (
      <Text fontSize="xs" color="gray.400">
        None
      </Text>
    );

  return (
    <Stack gap={1.5}>
      {Array.from(grouped.entries()).map(([cotabdId, aIds]) => {
        const cotabd = COTABDS.find(c => c.id === cotabdId);
        return (
          <Box key={cotabdId}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={0.5}>
              {cotabd?.name ?? cotabdId}
            </Text>
            <Flex gap={1} flexWrap="wrap">
              {aIds.map(aId => {
                const assertion = ASSERTIONS.find(a => a.id === aId);
                return (
                  <Badge key={aId} variant="outline" size="sm">
                    {assertion?.name ?? aId}
                  </Badge>
                );
              })}
            </Flex>
          </Box>
        );
      })}
    </Stack>
  );
});

const ActionMenu = memo(({onDelete}: {onDelete: () => void}) => (
  <MenuRoot>
    <MenuTrigger asChild>
      <IconButton size="xs" variant="ghost" aria-label="More actions">
        <Icon asChild>
          <EllipsisVerticalIcon />
        </Icon>
      </IconButton>
    </MenuTrigger>
    <MenuContent>
      <MenuItem value="edit">
        <Icon asChild>
          <PencilSquareIcon />
        </Icon>
        Edit
      </MenuItem>
      <MenuItem value="delete" color="fg.error" _hover={{bg: "bg.error", color: "fg.error"}} onClick={onDelete}>
        <Icon asChild>
          <TrashIcon />
        </Icon>
        Delete
      </MenuItem>
    </MenuContent>
  </MenuRoot>
));

const formatDate = (date: Date | null): string => {
  if (!date) return "\u2014";
  return date.toLocaleDateString("en-GB", {day: "numeric", month: "short", year: "numeric"});
};

const EvaluationBar = memo(({score, label}: {score: EvaluationScore; label: string}) => {
  const level = score != null ? SCORE_LEVELS[score - 1] : null;
  return (
    <Stack gap={0}>
      <Text fontSize="xs" color="gray.500">
        {label}
      </Text>
      {level ? (
        <HStack gap={1.5}>
          <Circle size="8px" bg={level.color} flexShrink={0} />
          <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
            {level.label}
          </Text>
        </HStack>
      ) : (
        <Text fontSize="xs" color="gray.400">
          {"\u2014"}
        </Text>
      )}
    </Stack>
  );
});

const ScoreCell = memo(({score, label}: {score: Risk["inherentScore"]; label: string}) =>
  score ? (
    <RiskScorePopover score={score} label={label} />
  ) : (
    <Text fontSize="sm" color="gray.400">
      {"\u2014"}
    </Text>
  ),
);

export const IdentifiedRisks = memo(
  ({
    risks,
    addRisk,
    deleteRisk,
    updateRiskStatus,
    isSimplified,
  }: Pick<RisksControlsCallbacks, "risks" | "addRisk" | "deleteRisk" | "updateRiskStatus"> & {
    isSimplified?: boolean;
  }) => {
    const navigate = useNavigate();
    const riskGridColumns = isSimplified ? "1fr 180px 180px 180px 58px" : RISK_GRID_COLUMNS;
    return (
      <Stack gap={0}>
        {/* Action bar */}
        <DataGrid.ActionBar>
          <Button
            size="xs"
            variant="outline"
            bg="white"
            borderColor="gray.200"
            color="gray.700"
            _hover={{bg: "gray.50"}}
            onClick={addRisk}
          >
            <Box as={PlusIcon} boxSize="14px" />
            Add risk
          </Button>
        </DataGrid.ActionBar>

        {/* Filter bar */}
        <Box bg="gray.50">
          <MockFilterBar total={risks.length} />
        </Box>

        {/* Scrollable table */}
        <DataGrid.ScrollArea minW="1100px">
          <DataGrid.Header columns={riskGridColumns}>
            <Text>Risk</Text>
            <Text whiteSpace="nowrap">Inherent Level</Text>
            <Text whiteSpace="nowrap">Residual Level</Text>
            <Text>Status</Text>
            {!isSimplified && <Text>COTABDs</Text>}
            <Box />
          </DataGrid.Header>

          {risks.map(risk => (
            <DataGrid.Row
              key={risk.id}
              columns={riskGridColumns}
              borderVariant="always"
              onClick={() => navigate(`../risks/${risk.id}`, {relative: "path"})}
            >
              <Stack gap={0.5} pr={3}>
                <Text fontSize="xs" color="gray.400">
                  {risk.riskNumber}
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {risk.title}
                </Text>
                <Text fontSize="xs" color="gray.500" lineClamp={2}>
                  {risk.description}
                </Text>
                <Text fontSize="xs" color="gray.400" mt={0.5}>
                  {risk.controls.length} mitigating control{risk.controls.length !== 1 ? "s" : ""}
                </Text>
              </Stack>
              <Box>
                <ScoreCell score={risk.inherentScore} label="Inherent Risk" />
              </Box>
              <Box>
                <ScoreCell score={risk.residualScore} label="Residual Risk" />
              </Box>
              <Box onClick={e => e.stopPropagation()}>
                <StatusPicker status={risk.status} onStatusChange={s => updateRiskStatus(risk.id, s)} />
              </Box>
              {!isSimplified && (
                <Box>
                  <CotabdCell cotabdAssertions={risk.cotabdAssertions} />
                </Box>
              )}
              <HStack gap={1}>
                <Box onClick={e => e.stopPropagation()}>
                  <ActionMenu onDelete={() => deleteRisk(risk.id)} />
                </Box>
                <Icon asChild boxSize="18px" color="gray.400">
                  <ChevronRightIcon />
                </Icon>
              </HStack>
            </DataGrid.Row>
          ))}
        </DataGrid.ScrollArea>
      </Stack>
    );
  },
);

export const IdentifiedControls = memo(
  ({
    allControls,
    addControl,
    deleteControl,
    updateControlStatus,
  }: Pick<RisksControlsCallbacks, "allControls" | "addControl" | "deleteControl" | "updateControlStatus">) => {
    const navigate = useNavigate();
    return (
      <Stack gap={0}>
        {/* Action bar */}
        <DataGrid.ActionBar>
          <Button
            size="xs"
            variant="outline"
            bg="white"
            borderColor="gray.200"
            color="gray.700"
            _hover={{bg: "gray.50"}}
            onClick={addControl}
          >
            <Box as={PlusIcon} boxSize="14px" />
            Add control
          </Button>
        </DataGrid.ActionBar>

        {/* Filter bar */}
        <Box bg="gray.50">
          <MockFilterBar total={allControls.length} />
        </Box>

        {/* Scrollable table */}
        <DataGrid.ScrollArea minW="900px">
          <DataGrid.Header columns={CONTROL_GRID_COLUMNS}>
            <Text>Control</Text>
            <Text>Evaluation</Text>
            <Text>Status</Text>
            <Text whiteSpace="nowrap">Last Assessed</Text>
            <Box />
          </DataGrid.Header>

          {allControls.map(ctrl => (
            <DataGrid.Row
              key={ctrl.id}
              columns={CONTROL_GRID_COLUMNS}
              borderVariant="always"
              onClick={() => navigate(`../controls/${ctrl.id}`, {relative: "path"})}
            >
              <Stack gap={0.5} pr={3}>
                <Text fontSize="xs" color="gray.400">
                  {ctrl.controlNumber}
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {ctrl.title}
                </Text>
                <Text fontSize="xs" color="gray.500" lineClamp={2}>
                  {ctrl.description}
                </Text>
              </Stack>
              <Stack gap={3}>
                <EvaluationBar score={ctrl.designScore} label="Design" />
                <EvaluationBar score={ctrl.implementationScore} label="Implementation" />
              </Stack>
              <Box onClick={e => e.stopPropagation()}>
                <StatusPicker status={ctrl.status} onStatusChange={s => updateControlStatus(ctrl.id, s)} />
              </Box>
              <Text fontSize="sm" color="gray.600">
                {formatDate(ctrl.lastAssessed)}
              </Text>
              <HStack gap={1}>
                <Box onClick={e => e.stopPropagation()}>
                  <ActionMenu onDelete={() => deleteControl(ctrl.id)} />
                </Box>
                <Icon asChild boxSize="18px" color="gray.400">
                  <ChevronRightIcon />
                </Icon>
              </HStack>
            </DataGrid.Row>
          ))}
        </DataGrid.ScrollArea>
      </Stack>
    );
  },
);

export const RiskControlMatrix = memo(
  ({risks, allControls, toggleMapping}: Pick<RisksControlsCallbacks, "risks" | "allControls" | "toggleMapping">) => {
    const [hoveredControlId, setHoveredControlId] = useState<string | null>(null);

    const mapping = useMemo(() => {
      const set = new Set<string>();
      for (const risk of risks) {
        for (const control of risk.controls) {
          set.add(`${risk.id}::${control.id}`);
        }
      }
      return set;
    }, [risks]);

    const controlRiskCounts = useMemo(() => {
      const counts: Record<string, number> = {};
      for (const ctrl of allControls) {
        counts[ctrl.id] = risks.filter(r => mapping.has(`${r.id}::${ctrl.id}`)).length;
      }
      return counts;
    }, [allControls, risks, mapping]);

    if (allControls.length === 0 || risks.length === 0) {
      return (
        <Text fontSize="sm" color="gray.500" p={6}>
          Add risks and controls to see the mapping matrix.
        </Text>
      );
    }

    return (
      <Box overflow="auto">
        <Table.Root size="sm">
          <Table.Header>
            {/* Top header row */}
            <Table.Row>
              <Table.ColumnHeader color="gray.500" fontSize="sm" px={3} borderRight="1px solid {colors.gray.100}">
                Risk
              </Table.ColumnHeader>
              <Table.ColumnHeader
                colSpan={allControls.length}
                textAlign="center"
                color="gray.500"
                fontSize="sm"
                px={2}
                borderRight="1px solid {colors.gray.100}"
              >
                Controls
              </Table.ColumnHeader>
              <Table.ColumnHeader color="gray.500" fontSize="sm" px={2} textAlign="center">
                #
              </Table.ColumnHeader>
            </Table.Row>
            {/* Sub header row */}
            <Table.Row>
              <Table.ColumnHeader color="gray.400" fontSize="xs" px={3} borderRight="1px solid {colors.gray.100}">
                Title
              </Table.ColumnHeader>
              {allControls.map(ctrl => (
                <Table.ColumnHeader
                  key={ctrl.id}
                  px={2}
                  textAlign="center"
                  whiteSpace="nowrap"
                  bg={hoveredControlId === ctrl.id ? "blue.50" : undefined}
                  onMouseEnter={() => setHoveredControlId(ctrl.id)}
                  onMouseLeave={() => setHoveredControlId(null)}
                  borderRight="1px solid {colors.gray.100}"
                >
                  <Text fontSize="xs" color="blue.600" fontWeight="medium">
                    {ctrl.controlNumber}
                  </Text>
                </Table.ColumnHeader>
              ))}
              <Table.ColumnHeader color="gray.400" fontSize="xs" px={2} textAlign="center">
                Count
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {risks.map(risk => (
              <Table.Row key={risk.id} _hover={{bg: "blue.50"}}>
                <Table.Cell px={3} whiteSpace="nowrap" maxW="220px" borderRight="1px solid {colors.gray.100}">
                  <HStack gap={1.5}>
                    <Text fontSize="xs" color="gray.400" flexShrink={0}>
                      {risk.riskNumber}
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" color="blue.600" truncate>
                      {risk.title}
                    </Text>
                  </HStack>
                </Table.Cell>
                {allControls.map(ctrl => (
                  <Table.Cell
                    key={ctrl.id}
                    px={2}
                    textAlign="center"
                    cursor="pointer"
                    bg={hoveredControlId === ctrl.id ? "blue.50" : undefined}
                    onMouseEnter={() => setHoveredControlId(ctrl.id)}
                    onMouseLeave={() => setHoveredControlId(null)}
                    borderRight="1px solid {colors.gray.100}"
                    onClick={() => toggleMapping(risk.id, ctrl.id)}
                  >
                    {mapping.has(`${risk.id}::${ctrl.id}`) ? (
                      <Box as="span" display="inline-block" w="8px" h="8px" borderRadius="full" bg="blue.500" />
                    ) : (
                      <Box as="span" display="inline-block" w="8px" h="8px" borderRadius="full" bg="transparent" />
                    )}
                  </Table.Cell>
                ))}
                <Table.Cell px={2} textAlign="center" fontSize="sm" color="gray.600" fontWeight="medium">
                  {risk.controls.length}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row css={{"& td": {borderBottom: "none"}}}>
              <Table.Cell
                px={3}
                fontSize="sm"
                fontWeight="semibold"
                color="gray.600"
                borderRight="1px solid {colors.gray.100}"
              >
                Count
              </Table.Cell>
              {allControls.map(ctrl => (
                <Table.Cell
                  key={ctrl.id}
                  px={2}
                  textAlign="center"
                  fontSize="sm"
                  fontWeight="medium"
                  color={controlRiskCounts[ctrl.id] === 0 ? "orange.600" : "gray.600"}
                  bg={hoveredControlId === ctrl.id ? "blue.50" : undefined}
                  onMouseEnter={() => setHoveredControlId(ctrl.id)}
                  onMouseLeave={() => setHoveredControlId(null)}
                  borderRight="1px solid {colors.gray.100}"
                >
                  {controlRiskCounts[ctrl.id]}
                </Table.Cell>
              ))}
              <Table.Cell px={2} />
            </Table.Row>
          </Table.Footer>
        </Table.Root>
      </Box>
    );
  },
);

function useProcessRisksState(processId: string) {
  const {onItemStatusChange} = useOutletContext<ProcessesOutletContext>();
  const initialRisks = useMemo(
    () => PROCESS_RISKS_AND_CONTROLS.find(r => r.processId === processId)?.risks ?? [],
    [processId],
  );
  return useRisksState(initialRisks, onItemStatusChange);
}

export const ProcessRisks = memo(({headerProps}: {headerProps: ProcessHeaderProps}) => {
  const {risks, addRisk, deleteRisk, updateRiskStatus} = useProcessRisksState(headerProps.process.id);
  const {isSimplified} = useOutletContext<ProcessesOutletContext>();
  return (
    <Flex direction="column" minH={0} flex={1}>
      <ProcessHeader {...headerProps} />
      <IdentifiedRisks
        risks={risks}
        addRisk={addRisk}
        deleteRisk={deleteRisk}
        updateRiskStatus={updateRiskStatus}
        isSimplified={isSimplified}
      />
    </Flex>
  );
});

export const ProcessControls = memo(({headerProps}: {headerProps: ProcessHeaderProps}) => {
  const {allControls, addControl, deleteControl, updateControlStatus} = useProcessRisksState(headerProps.process.id);
  return (
    <Flex direction="column" minH={0} flex={1}>
      <ProcessHeader {...headerProps} />
      <IdentifiedControls
        allControls={allControls}
        addControl={addControl}
        deleteControl={deleteControl}
        updateControlStatus={updateControlStatus}
      />
    </Flex>
  );
});

export const ProcessRiskControlMatrix = memo(({headerProps}: {headerProps: ProcessHeaderProps}) => {
  const {risks, allControls, toggleMapping} = useProcessRisksState(headerProps.process.id);
  return (
    <Flex direction="column" minH={0} flex={1}>
      <ProcessHeader {...headerProps} />
      <RiskControlMatrix risks={risks} allControls={allControls} toggleMapping={toggleMapping} />
    </Flex>
  );
});

function useProcessFindingsState(processId: string) {
  const initialFindings = useMemo(
    () => PROCESS_FINDINGS.find(f => f.processId === processId)?.findings ?? [],
    [processId],
  );
  return useFindingsState(initialFindings);
}

export const ProcessFindings = memo(({headerProps}: {headerProps: ProcessHeaderProps}) => {
  const findingsState = useProcessFindingsState(headerProps.process.id);
  const {allControls} = useProcessRisksState(headerProps.process.id);
  return (
    <Flex direction="column" minH={0} flex={1}>
      <ProcessHeader {...headerProps} />
      <Box p={6} bg="gray.50" flex={1}>
        <FindingsCardList {...findingsState} allControls={allControls} />
      </Box>
    </Flex>
  );
});
